import react, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/AssessmentListStyles.css";
import Logger from "../../../utils/Logger";
import { LocalStorage } from "../../../utils/Utils";
import strings from "../../../utils/Strings";
import { useNavigate } from "react-router-dom";
import { useAlert } from "../../../components/AlertContextComponent";
import { useApi } from "../../../service/ApiContext";
import CardQuesIcon from "../assets/allAssessment/cardQuesIcon.png";
import CardSubIcon from "../assets/allAssessment/cardSubIcon.png";
import CardClockIcon from "../assets/allAssessment/cardClockIcon.png";
import CardPositiveClock from "../assets/allAssessment/cardPositiveClock.svg";
import StartClock from "../assets/allAssessment/startClock.png";
import EndClock from "../assets/allAssessment/endClock.png";
import CardNegativeClock from "../assets/allAssessment/cardNegativeClock.svg";
import newTabIcon from "../assets/allAssessment/new-tab 1.svg";
import loader from "../../../utils/Utils";
import missed from "../assets/allAssessment/missedIcon.png";
import completed from "../assets/allAssessment/completedIcon.png";
import needhelpImg from "../assets/allAssessment/needHelpImg.png";

import {
  AssessmentEntity,
  AssessmentListResponse,
  CompletedAssessment,
  CompletedAssessmentsResponse,
} from "../data/response/AssessmentStatusResponse";
import { AssessmentQuestionModuleClient } from "../../dashboard/data/client/VideoReportClient";
import {
  setAssessmentId,
  setCurrentCounter,
  setDuration,
  setIsIndependent,
  setStatus,
  setTimeSpend,
  setTotalQuestion,
} from "../data/redux/AssessmentSlice";
import { useDispatch, useSelector } from "react-redux";
import CommonUtils from "../../../utils/Common_Utils";
import CustomTooltip from "../../../components/CustomToolTipComponents";
import CardBottomOverlay from "../../dashboard/components/CardBottomOverlay";
import dayjs from "dayjs";
import { setSelectedToggle } from "../../dashboard/data/redux/GradedPopQuizRedux";
import RightModalRecommendation from "../../recommendation/needHelp/pages/NeedHelpRecommendation";
import { logActionEvent } from "../../../analytics/analytics_utils";
import analytics_strings from "../../../analytics/analytics_strings";
import { RootState } from "../../../store/store";

interface SubjectPopupData {
  title: string;
  total: number;
  jozuna: string[];
  curriculum: string[];
  industry: string[];
}

interface AssessmentListPageProps {
  sidebarExpanded?: boolean;
}

const AssessmentListPage: React.FC<AssessmentListPageProps> = ({
  sidebarExpanded,
}) => {
  const [activeTab, setActiveTab] = useState<"active" | "upcoming" | "expired">(
    "active",
  );
  const userData = useSelector((state: RootState) => state.profile.profileKeys);

  const [isModalOpen, setModalOpen] = useState(false);
  const toggleModal = () => {
    setModalOpen(!isModalOpen);
    if (!isModalOpen) {
      logActionEvent(
        analytics_strings.analytics_events.reccomendation_needHelp,
        {
          candidate_id: userData.candidate_id,
        },
      );
    }
  };
  const dispatch = useDispatch();
  const api = useApi(); // Initialize the custom hook for API requests
  const [isErrorPopup, setIsErrorPopup] = useState(true);
  const [assessmentList, setAssessmentList] = useState<AssessmentEntity[]>([]);
  const [assessmentCompletedList, setAssessmentCompletedList] = useState<
    CompletedAssessment[]
  >([]);
  const isGradedQuiz = useSelector(
    (state: RootState) => state.advancedFilterGradedPopQuiz.is_graded_quiz,
  );
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const IsIndependentUser =
    LocalStorage.getLocalData("institute_id") === "null"; //No need to show remaining time for independent user
  const [showSubjectPopup, setShowSubjectPopup] = useState(false);

  const [selectedSubjects, setSelectedSubjects] =
    useState<SubjectPopupData | null>(null);

  const getAssessmentEntityId = (item: any): string => {
    return String(item?.assessment_entity_id || item?.assessment_id || "").trim();
  };

  const getAssessmentTitle = (item: any): string => {
    return String(
      item?.assessment_title ||
      item?.assessment_entity_name ||
      item?.assessment_name ||
      "",
    ).trim();
  };

  useEffect(() => {
    fetchAssessmentStatus();
  }, []);

  const fetchAssessmentStatus = async () => {
    loader.showLoading();
    api.setAuthToken(LocalStorage.getLocalData("access_token"));
    try {
      const response = await api.get(
        AssessmentQuestionModuleClient.list_assessment,
      );
      const data = response.data as AssessmentListResponse;
      let uniqueAssessments: AssessmentEntity[] = [];

      if (data?.list_assessments?.length > 0) {
        setIsErrorPopup(false);

        // Deduplicate assessments by assessment_entity_id and assessment_title
        const seenAssessmentKeys = new Set<string>();
        const seenAssessmentEntityIds = new Set<string>();

        uniqueAssessments = data.list_assessments.filter((item: AssessmentEntity) => {
          const entityId = getAssessmentEntityId(item);
          const title = getAssessmentTitle(item);
          const key = `${entityId}:::${title}`;

          if (seenAssessmentKeys.has(key) || (entityId && seenAssessmentEntityIds.has(entityId))) {
            return false;
          }

          seenAssessmentKeys.add(key);
          if (entityId) {
            seenAssessmentEntityIds.add(entityId);
          }
          return true;
        });

        setAssessmentList(uniqueAssessments);
        Logger.info("✅ Assessment data fetched:", uniqueAssessments);
      } else {
        Logger.info("⚠️ No assessments returned");
        showAlert({
          show: true,
          title: "Info",
          message: data.error,
          alertType: "info",
          onlyNeedOneButton: true,
          button1Text: "Cancel",
          btn1onPressed() {
            navigate("/dashboard/assessment");
          },
        });
      }
      // Pick relevant assessment to proceed with
      const assessmentPool = uniqueAssessments.length > 0 ? uniqueAssessments : data?.list_assessments || [];
      const selected =
        assessmentPool.find(
          (a: any) =>
            a.candidate_assessment_status ===
            strings.assessment_status.initated ||
            a.candidate_assessment_status ===
            strings.assessment_status.inProgress,
        ) || assessmentPool[0]; // fallback to first if none match

      if (!selected) {
        setIsErrorPopup(true);
        showAlert({
          show: true,
          title: "Info",
          message: data.error,
          alertType: "info",
          onlyNeedOneButton: true,
          button1Text: "Cancel",
          btn1onPressed() {
            navigate("/dashboard/assessment");
          },
        });
        return;
      }

      // Proceed with previous logic
      if (
        selected.candidate_assessment_status ===
        strings.assessment_status.initated ||
        selected.candidate_assessment_status ===
        strings.assessment_status.inProgress
      ) {
        dispatch(setAssessmentId(selected.assessment_id || ""));
        dispatch(setCurrentCounter(selected.current_counter));
        dispatch(setDuration(selected.assessment_duration));
        dispatch(setStatus(selected.candidate_assessment_status));
        dispatch(setTimeSpend(selected.time_spend));
        dispatch(setTotalQuestion(selected.total_question));
        dispatch(setIsIndependent(selected.isIndependent));

        LocalStorage.setLocalData(
          strings.localStorage_keys.assessment_details.duration,
          selected.assessment_duration.toString(),
        );
        LocalStorage.setLocalData(
          strings.localStorage_keys.assessment_details.time_spend,
          selected.time_spend.toString(),
        );
        LocalStorage.setLocalData(
          strings.localStorage_keys.assessment_details
            .candidate_assessment_status,
          selected.candidate_assessment_status,
        );
        LocalStorage.setLocalData(
          strings.localStorage_keys.assessment_details
            .candidate_assessment_status,
          selected.candidate_assessment_status,
        );
        LocalStorage.setLocalData(
          strings.localStorage_keys.assessment_details.assessment_id,
          selected.assessment_id || "",
        );
        LocalStorage.setLocalData(
          strings.localStorage_keys.assessment_details.current_counter,
          selected.current_counter.toString(),
        );
        LocalStorage.setLocalData(
          strings.localStorage_keys.assessment_details.total_question,
          selected.total_question.toString(),
        );
        LocalStorage.setLocalData(
          strings.localStorage_keys.assessment_details.isIndependent,
          selected.isIndependent?.toString() ?? "true",
        );
      } else if (
        selected.candidate_assessment_status ===
        strings.assessment_status.notStarted
      ) {
        dispatch(setDuration(selected.assessment_duration));
        dispatch(setTotalQuestion(selected.total_question));
        dispatch(setIsIndependent(selected.isIndependent));
        dispatch(setStatus(selected.candidate_assessment_status));

        LocalStorage.setLocalData(
          strings.localStorage_keys.assessment_details.duration,
          selected.assessment_duration.toString(),
        );
        LocalStorage.setLocalData(
          strings.localStorage_keys.assessment_details
            .candidate_assessment_status,
          selected.candidate_assessment_status,
        );
        LocalStorage.setLocalData(
          strings.localStorage_keys.assessment_details
            .candidate_assessment_status,
          selected.candidate_assessment_status,
        );
        Logger.info(
          "$$$$$$$$$$$$$$$$$$$$$$$$$$",
          selected.candidate_assessment_status,
        );
        LocalStorage.setLocalData(
          strings.localStorage_keys.assessment_details.isIndependent,
          selected.isIndependent?.toString() ?? "true",
        );
        Logger.info("Assessment not started");
      } else if (data.error) {
        setIsErrorPopup(true);
        showAlert({
          show: true,
          title: "Info",
          message: data.error,
          alertType: "info",
          onlyNeedOneButton: true,
          button1Text: "Cancel",
          btn1onPressed() {
            navigate("/dashboard/assessment");
          },
        });
        Logger.info("Assessment ended or invalid status");
      }
    } catch (error) {
      Logger.error("Error fetching assessment status:", error);
      setIsErrorPopup(true);
    } finally {
      loader.dismissLoading();
    }
  };
  const [selectedStatus, setSelectedStatus] = useState<
    "ALL" | "COMPLETED" | "MISSED"
  >("ALL");
  const filteredAssessmentList = useMemo(() => {
    console.log("Filtering...", selectedStatus);

    let list = assessmentCompletedList;
    if (selectedStatus !== "ALL") {
      list = assessmentCompletedList.filter(
        (item) => item.status === selectedStatus,
      );
    }

    const seenKeys = new Set<string>();
    const seenEntityIds = new Set<string>();

    return list.filter((item) => {
      const entityId = getAssessmentEntityId(item);
      const title = getAssessmentTitle(item);
      const key = `${entityId}:::${title}`;

      if (seenKeys.has(key) || (entityId && seenEntityIds.has(entityId))) {
        return false;
      }

      seenKeys.add(key);
      if (entityId) {
        seenEntityIds.add(entityId);
      }
      return true;
    });
  }, [assessmentCompletedList, selectedStatus]);

  useEffect(() => {
    console.log(
      assessmentCompletedList.map((item) => ({
        status: item.status,
        type: typeof item.status,
      })),
    );
  }, [assessmentCompletedList]);
  useEffect(() => {
    console.log("Selected Status:", selectedStatus);
  }, [selectedStatus]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const fetchCompletedAssessmentStatus = async () => {
    // if (loading || !hasMore) return;

    setLoading(true);
    loader.showLoading();

    const limit = 25;
    api.setAuthToken(LocalStorage.getLocalData("access_token"));

    try {
      const response = await api.get(
        `${AssessmentQuestionModuleClient.completed_list_assessment}?page_number=${currentPage}&limit=${limit}&status=${activeTab === "upcoming" ? "upcoming" : "completed"}&offset_time=${CommonUtils.formatLocalTime()}`,
      );
      const staticAssessmentData: CompletedAssessmentsResponse = {
        no_of_assessments: 2,
        list_completed_assessments: [
          {
            assessment_id: "006b14df-8318-4456-8c1c-8487412464ac",
            assessment_name: "revamp check assessment",
            status: "MISSED",
            number_of_skill_questions: 13,
            number_of_category_questions: 0,
            number_of_curriculum_questions: 0,
            dimension_descriptive_questions: 1,
            total_question: 13,
            duration: 60,
            category_list: [],
            curriculum_list: [],
            assessment_start_time: "2026/04/10, 07:01:47",
            assessment_expiry: 691231,
          },
          {
            assessment_id: "25595019-5636-4af3-b60d-4d67f9abcbcd",
            assessment_name: "Sample 2",
            status: "COMPLETED",
            number_of_skill_questions: 0,
            number_of_category_questions: 0,
            number_of_curriculum_questions: 1,
            dimension_descriptive_questions: 0,
            total_question: 1,
            duration: 20,
            category_list: [],
            curriculum_list: [],
            assessment_start_time: "2026/01/07, 09:45:13",
            assessment_expiry: 86344,
          },
        ],
      };
      const data = response.data as CompletedAssessmentsResponse;

      if (data?.list_completed_assessments?.length > 0) {
        // Collect active assessments from /assessment/list to prevent duplicating them in completed/expired list
        const activeEntityIds = new Set(
          assessmentList
            .filter((a: any) => a.status === "Start" || a.status === "Resume")
            .map((a: any) => getAssessmentEntityId(a))
            .filter(Boolean),
        );
        const activeKeys = new Set(
          assessmentList
            .filter((a: any) => a.status === "Start" || a.status === "Resume")
            .map((a: any) => `${getAssessmentEntityId(a)}:::${getAssessmentTitle(a)}`),
        );

        // Deduplicate incoming batch by assessment_entity_id and assessment_title
        const seenBatchKeys = new Set<string>();
        const seenBatchEntityIds = new Set<string>();

        const uniqueNewRecords = data.list_completed_assessments.filter((item: CompletedAssessment) => {
          const entityId = getAssessmentEntityId(item);
          const title = getAssessmentTitle(item);
          const key = `${entityId}:::${title}`;

          // Avoid duplicating active assessments in completed/expired list
          if (
            (entityId && activeEntityIds.has(entityId)) ||
            (key && activeKeys.has(key))
          ) {
            return false;
          }

          // Avoid duplicates within this batch
          if (
            seenBatchKeys.has(key) ||
            (entityId && seenBatchEntityIds.has(entityId))
          ) {
            return false;
          }

          seenBatchKeys.add(key);
          if (entityId) {
            seenBatchEntityIds.add(entityId);
          }
          return true;
        });

        if (currentPage === 1) {
          setAssessmentCompletedList(uniqueNewRecords);
        } else {
          setAssessmentCompletedList((prev) => {
            const existingKeys = new Set(
              prev.map((item: CompletedAssessment) => `${getAssessmentEntityId(item)}:::${getAssessmentTitle(item)}`),
            );
            const existingEntityIds = new Set(
              prev.map((item: CompletedAssessment) => getAssessmentEntityId(item)).filter(Boolean),
            );

            const appendUnique = uniqueNewRecords.filter((item: CompletedAssessment) => {
              const entityId = getAssessmentEntityId(item);
              const title = getAssessmentTitle(item);
              const itemKey = `${entityId}:::${title}`;

              const isDuplicate =
                existingKeys.has(itemKey) ||
                (entityId && existingEntityIds.has(entityId));

              if (isDuplicate) {
                return false;
              }

              existingKeys.add(itemKey);
              if (entityId) {
                existingEntityIds.add(entityId);
              }
              return true;
            });

            return [...prev, ...appendUnique];
          });
        }

        if (data.list_completed_assessments.length < limit) {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (error) {
      Logger.error("Error fetching assessment status:", error);
      setIsErrorPopup(true);
    } finally {
      setLoading(false);
      loader.dismissLoading();
    }
  };
  useEffect(() => {
    setAssessmentCompletedList([]);
    setCurrentPage(1);
    setHasMore(true);
  }, [activeTab]);
  useEffect(() => {
    if (activeTab === "expired" || activeTab === "upcoming") {
      fetchCompletedAssessmentStatus();
    }
  }, [currentPage, activeTab]);
  useEffect(() => {
    const handleScroll = () => {
      const el = containerRef.current;

      // ONLY run for completed tab
      if (!el || loading || !hasMore || activeTab !== "expired") return;

      if (el.scrollTop + el.clientHeight + 100 >= el.scrollHeight) {
        setCurrentPage((prev) => prev + 1);
      }
    };

    const el = containerRef.current;
    el?.addEventListener("scroll", handleScroll);

    return () => el?.removeEventListener("scroll", handleScroll);
  }, [loading, hasMore, activeTab]);
  const getTimeDisplay = (time: string) => {
    const [days, hours, minutes] = time.split(":").map(Number);

    if (days > 0) {
      return {
        text: `${days} Day${days > 1 ? "s" : ""} Remaining`,
        urgent: false,
      };
    }

    if (hours > 0) {
      return {
        text: `${hours} Hour${hours > 1 ? "s" : ""} Remaining`,
        urgent: true,
      };
    }

    return {
      text: `${minutes} Min Remaining`,
      urgent: true,
    };
  };

  const percentage = 75; // dynamic
  const activeAssessments = useMemo(() => {
    const seenKeys = new Set<string>();
    const seenEntityIds = new Set<string>();

    return assessmentList
      .filter((item) => item.status === "Start" || item.status === "Resume")
      .filter((item) => {
        const entityId = getAssessmentEntityId(item);
        const title = getAssessmentTitle(item);
        const key = `${entityId}:::${title}`;

        if (seenKeys.has(key) || (entityId && seenEntityIds.has(entityId))) {
          return false;
        }

        seenKeys.add(key);
        if (entityId) {
          seenEntityIds.add(entityId);
        }
        return true;
      });
  }, [assessmentList]);

  const upcomingAssessments = useMemo(() => {
    const seenKeys = new Set<string>();
    const seenEntityIds = new Set<string>();

    return assessmentCompletedList
      .filter((item) => item.status === "UPCOMING")
      .filter((item) => {
        const entityId = getAssessmentEntityId(item);
        const title = getAssessmentTitle(item);
        const key = `${entityId}:::${title}`;

        if (seenKeys.has(key) || (entityId && seenEntityIds.has(entityId))) {
          return false;
        }

        seenKeys.add(key);
        if (entityId) {
          seenEntityIds.add(entityId);
        }
        return true;
      });
  }, [assessmentCompletedList]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const statusOptions = [
    { label: "All", value: "ALL" },
    { label: "Completed", value: "COMPLETED" },
    { label: "Missed", value: "MISSED" },
  ];
  const completedAssessments = assessmentCompletedList.filter(
    (item) => item.status === "COMPLETED" || item.status === "SCORED",
  );
  const formatDuration = (minutes: number) => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hrs > 0) {
      return `${hrs}:${String(mins).padStart(2, "0")}:00`;
    }

    return `${mins}:00`;
  };
  useEffect(() => {
    console.log("Selected:", selectedStatus);
    console.log("Original:", assessmentCompletedList.length);
    console.log("Filtered:", filteredAssessmentList.length);
  }, [selectedStatus, assessmentCompletedList]);
  return (
    <div className="v2-listAssessmentPage">
      <div className="v2-listAssessmentHeader">
        <div
          className="v2-listHeaderLeft"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <div>
            <div className="v2-assessmentTitleText">Assessments</div>

            <div className="v2-assessmentSubText">
              Hey there, it’s time for a quick learning progress check.
            </div>
          </div>

          <img
            src={needhelpImg}
            style={{
              marginLeft: "auto",
              display: "block",
              height: "40px",
              width: "40px",
              cursor: "pointer",
            }}
            onClick={() => {
              setModalOpen(true);
            }}
          />
        </div>
        <div className="v2-listHeaderRight"></div>
      </div>
      <br></br>
      <div>
        <div
          style={{
            display: "flex",
            gap: "10px",
            marginLeft: "20px",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", gap: "10px", marginLeft: "20px" }}>
            <div
              onClick={() => setActiveTab("active")}
              className={`v2-AssessmentListTab ${activeTab === "active" ? "active" : ""
                }`}
            >
              <span
                style={{
                  height: "8px",
                  width: "8px",
                  backgroundColor: "red",
                  borderRadius: "50%",
                  display: "inline-block",
                  marginRight: "8px",
                }}
              />
              Active
            </div>

            <div
              onClick={() => setActiveTab("upcoming")}
              className={`v2-AssessmentListTab ${activeTab === "upcoming" ? "active" : ""}`}
            >
              Upcoming
            </div>

            <div
              onClick={() => {
                setActiveTab("expired");
              }}
              className={`v2-AssessmentListTab ${activeTab === "expired" ? "active" : ""}`}
            >
              Expired
            </div>
          </div>
          {/* status filter {completed or missed} */}
          {activeTab === "expired" && (
            <div className="assessmentDropdown_v2">
              <div
                className="assessmentDropdownHeader_v2"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <span>
                  {
                    statusOptions.find((item) => item.value === selectedStatus)
                      ?.label
                  }
                </span>

                <span
                  className={`assessmentDropdownArrow_v2 ${isDropdownOpen ? "rotate_v2" : ""
                    }`}
                >
                  ▼
                </span>
              </div>

              {isDropdownOpen && (
                <div className="assessmentDropdownMenu_v2">
                  {statusOptions.map((item) => (
                    <div
                      key={item.value}
                      className={`assessmentDropdownItem_v2 ${selectedStatus === item.value ? "selectedItem_v2" : ""
                        }`}
                      onClick={() => {
                        setSelectedStatus(
                          item.value as "ALL" | "COMPLETED" | "MISSED",
                        );
                        setIsDropdownOpen(false);
                      }}
                    >
                      {item.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}{" "}
        </div>
        <div
          ref={containerRef}
          className="v2-listAssessmentContent"
          style={{ height: "80vh", overflow: "auto" }}
        >
          <div
            className={`v2-assessmentListCardsWrap ${sidebarExpanded ? "sidebar-expanded" : "sidebar-collapsed"
              }`}
          >
            {activeTab === "active" && (
              <>
                {activeAssessments.length === 0 ? (
                  <div
                    style={{
                      width: "100%",
                      height: "calc(100vh - 220px)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginLeft: "100%",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "28px",
                        fontWeight: 600,
                        color: "#666",
                        textAlign: "center",
                      }}
                    >
                      No Active Assessments Found
                    </div>
                  </div>
                ) : (
                  activeAssessments.map((item: AssessmentEntity, index: number) => {
                    const [days, hours, minutes] =
                      item.assessment_entity_time_left.split(":").map(Number);

                    const isUrgent = days === 0 && hours < 24;

                    const getTimeText = () => {
                      if (days > 0)
                        return `${days} Day${days > 1 ? "s" : ""} Remaining`;

                      if (hours > 0)
                        return `${hours} Hour${hours > 1 ? "s" : ""} Remaining`;

                      return `${minutes} Min Remaining`;
                    };

                    const subjectsList = [
                      ...(item.dimension_questions ? ["Jozuna"] : []),
                      ...(item.category_list || []),
                      ...(item.curriculum_list || []),
                    ];

                    return (
                      <div
                        key={item.assessment_entity_id || item.assessment_id || `active-${index}`}
                        className={`v2-assessment-card ${isUrgent ? "v2-card-urgent" : "v2-card-urgentActive"
                          }`}
                      >
                        {/* TOP */}
                        <div className="v2-card-top">
                          <div className="v2-assessment-badges">
                            {item.dimension_questions && (
                              <span className="v2-assessment-badge">
                                JOZUNA
                              </span>
                            )}

                            {item.curriculum_questions && (
                              <span className="v2-assessment-badge">
                                Industry
                              </span>
                            )}

                            {item.category_questions && (
                              <span className="v2-assessment-badge">
                                Curriculum
                              </span>
                            )}
                          </div>

                          {/* {item.status === "Resume" && (
                            <div className="v2-assessment-progress">
                              <svg width="52" height="52">
                                <circle
                                  className="progress-bg"
                                  cx="26"
                                  cy="26"
                                  r="20"
                                  strokeWidth="4"
                                />

                                <circle
                                  className="progress-bar"
                                  cx="26"
                                  cy="26"
                                  r="20"
                                  strokeWidth="4"
                                  strokeDasharray={2 * Math.PI * 20}
                                  strokeDashoffset={
                                    2 * Math.PI * 20 * (1 - percentage / 100)
                                  }
                                />
                              </svg>

                              <div className="progress-text">{percentage}%</div>
                            </div>
                          )} */}
                        </div>

                        {/* TITLE */}
                        <h3 className="v2-assessment-title">
                          {getAssessmentTitle(item)}
                        </h3>

                        <div className="v2-listAssessmentdivider"></div>

                        {/* DETAILS */}
                        <div className="v2-card-details">
                          <div className="v2-card-row-inline">
                            <div className="v2-card-item">
                              <img src={CardClockIcon} />

                              <span
                                style={{
                                  color:
                                    Number(
                                      item.assessment_entity_time_left.split(
                                        ":",
                                      )[0],
                                    ) === 0 &&
                                      Number(
                                        item.assessment_entity_time_left.split(
                                          ":",
                                        )[1],
                                      ) <= 20
                                      ? "red"
                                      : "#02874c",
                                }}
                              >
                                {item.assessment_entity_time_left}

                                <span
                                  className="light-text"
                                  style={{ fontWeight: "700" }}
                                >
                                  {" "}
                                  / {formatDuration(item.assessment_duration)}
                                </span>
                              </span>
                            </div>

                            <div className="v2-card-item">
                              <img src={CardQuesIcon} />

                              <span>
                                <span
                                  style={{
                                    color: "#da940b",
                                    fontWeight: "700",
                                  }}
                                >
                                  {item.current_counter}
                                </span>{" "}
                                / {item.total_question}{" "}
                                <span className="light-text">Questions</span>
                              </span>
                            </div>
                          </div>

                          <div className="v2-card-row-inline">
                            <div className="v2-card-item">
                              <img src={StartClock} />
                              <span>{item.assessment_start_time}</span>
                            </div>

                            <div className="v2-card-item">
                              <img src={EndClock} />

                              {dayjs(item.assessment_start_time)
                                .add(item.assessment_expiry, "second")
                                .format("YYYY/MM/DD, HH:mm:ss")}
                            </div>
                          </div>

                          <div className="v2-card-item">
                            <img src={CardSubIcon} />

                            <span>
                              {subjectsList.length}

                              <span
                                style={{
                                  marginLeft: "2px",
                                  fontWeight: 300,
                                }}
                              >
                                {" "}
                                {subjectsList.length > 1
                                  ? "Subjects Distribution"
                                  : "Subject Distribution"}
                              </span>
                            </span>

                            {subjectsList.length > 1 && (
                              <span
                                className="v2-view-all"
                                onClick={() => {
                                  setSelectedSubjects({
                                    title: getAssessmentTitle(item),
                                    total:
                                      (item.dimension_questions ? 1 : 0) +
                                      (item.category_list?.length || 0) +
                                      (item.curriculum_list?.length || 0),

                                    jozuna: item.dimension_questions
                                      ? ["JOZUNA Skills"]
                                      : [],

                                    curriculum: (item.category_list || []).map(
                                      (c: any) => c.category_name,
                                    ),

                                    industry: (item.curriculum_list || []).map(
                                      (c: any) => c.curriculum_name,
                                    ),
                                  });

                                  setShowSubjectPopup(true);
                                }}
                              >
                                View All
                              </span>
                            )}
                          </div>
                        </div>

                        <br />

                        <div className="v2-card-bottom">
                          <button
                            className="v2-start-btn"
                            onClick={async () => {
                              const isIndependentCheck =
                                LocalStorage.getLocalData("isIndependent");

                              //   if (
                              //     item.status !== "Resume" &&
                              //     isIndependentCheck === "false"
                              //   ) {
                              //     //Check Trail Assessment Exceed for Start Assessment
                              //     const subscriptionFailed =
                              //       await checkSubscriptionValidation();
                              //     if (subscriptionFailed === true) {
                              //       return;
                              //     }
                              //   }
                              // ✅ Check status and set data accordingly
                              if (
                                item.candidate_assessment_status ===
                                strings.assessment_status.initated ||
                                item.candidate_assessment_status ===
                                strings.assessment_status.inProgress
                              ) {
                                dispatch(
                                  setAssessmentId(item.assessment_id || ""),
                                );
                                dispatch(
                                  setCurrentCounter(item.current_counter),
                                );
                                dispatch(setDuration(item.assessment_duration));
                                dispatch(
                                  setStatus(item.candidate_assessment_status),
                                );
                                dispatch(setTimeSpend(item.time_spend));
                                dispatch(setTotalQuestion(item.total_question));
                                dispatch(setIsIndependent(item.isIndependent));

                                LocalStorage.setLocalData(
                                  strings.localStorage_keys.assessment_details
                                    .duration,
                                  item.assessment_duration.toString(),
                                );
                                LocalStorage.setLocalData(
                                  strings.localStorage_keys.assessment_details
                                    .time_spend,
                                  item.time_spend.toString(),
                                );
                                LocalStorage.setLocalData(
                                  strings.localStorage_keys.assessment_details
                                    .candidate_assessment_status,
                                  item.candidate_assessment_status,
                                );
                                LocalStorage.setLocalData(
                                  strings.localStorage_keys.assessment_details
                                    .assessment_id,
                                  item.assessment_id || "",
                                );
                                LocalStorage.setLocalData(
                                  strings.localStorage_keys.assessment_details
                                    .current_counter,
                                  item.current_counter.toString(),
                                );
                                LocalStorage.setLocalData(
                                  strings.localStorage_keys.assessment_details
                                    .total_question,
                                  item.total_question.toString(),
                                );
                                LocalStorage.setLocalData(
                                  strings.localStorage_keys.assessment_details
                                    .isIndependent,
                                  item.isIndependent?.toString() ?? "true",
                                );
                              } else if (
                                item.candidate_assessment_status ===
                                strings.assessment_status.notStarted
                              ) {
                                dispatch(setDuration(item.assessment_duration));
                                dispatch(setTotalQuestion(item.total_question));
                                dispatch(setIsIndependent(item.isIndependent));
                                dispatch(
                                  setStatus(item.candidate_assessment_status),
                                );

                                LocalStorage.setLocalData(
                                  strings.localStorage_keys.assessment_details
                                    .duration,
                                  item.assessment_duration.toString(),
                                );
                                LocalStorage.setLocalData(
                                  strings.localStorage_keys.assessment_details
                                    .candidate_assessment_status,
                                  item.candidate_assessment_status,
                                );
                                LocalStorage.setLocalData(
                                  strings.localStorage_keys.assessment_details
                                    .isIndependent,
                                  item.isIndependent?.toString() ?? "true",
                                );

                                Logger.info(
                                  "$$$$$$$$$$$$$$$$$$$$$$$$$$",
                                  item.candidate_assessment_status,
                                );
                                Logger.info("Assessment not started");
                              }

                              // ✅ Navigate after setup
                              Logger.info(
                                "ASSESSMENT ##########",
                                item.assessment_entity_id,
                              );
                              LocalStorage.setLocalData(
                                strings.localStorage_keys.assessment_details
                                  .assessment_expiry_time,
                                item.assessment_expiry.toString(),
                              );
                              LocalStorage.setLocalData(
                                strings.localStorage_keys.assessment_details
                                  .assessment_entity_time_left,
                                item.assessment_entity_time_left.toString(),
                              );
                              LocalStorage.setLocalData(
                                strings.localStorage_keys.assessment_details
                                  .assessment_start_time,
                                item.assessment_start_time.toString(),
                              ); ///For End Time
                              LocalStorage.setLocalData(
                                strings.localStorage_keys.assessment_details
                                  .assessment_entity_id,
                                item.assessment_entity_id?.toString() || "",
                              );
                              if (item.is_camera_required !== undefined) {
                                LocalStorage.setLocalData(
                                  strings.localStorage_keys.assessment_details
                                    .is_camera_required,
                                  item.is_camera_required.toString(),
                                );
                              }
                              //Assesment Time Detection
                              const warningText =
                                CommonUtils.getAssessmentTimeWarning(
                                  item,
                                  false,
                                );
                              if (warningText) {
                                showAlert({
                                  show: true,
                                  title: "Warning",
                                  message: warningText,
                                  alertType: "error",
                                  onlyNeedOneButton: true,
                                  button1Text: "Ok",
                                  btn1onPressed() {
                                    navigate("cameracheck", {
                                      state: {
                                        assessmentList: assessmentList,
                                        selectedAssessment: item, // pass selected card
                                      },
                                    });
                                  },
                                });
                                return;
                              } else {
                                navigate("cameracheck", {
                                  state: {
                                    assessmentList: assessmentList,
                                    selectedAssessment: item, // pass selected card
                                  },
                                });
                              }
                            }}
                          >
                            {item.status}

                            <img
                              src={newTabIcon}
                              alt=""
                              style={{
                                height: "20px",
                                width: "20px",
                                marginLeft: "10px",
                              }}
                            />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </>
            )}
            {activeTab === "upcoming" && (
              <>
                {upcomingAssessments.length === 0 ? (
                  <div
                    style={{
                      width: "100%",
                      height: "calc(100vh - 220px)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginLeft: "100%",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "28px",
                        fontWeight: 600,
                        color: "#666",
                        textAlign: "center",
                      }}
                    >
                      No Upcoming Assessments Found
                    </div>
                  </div>
                ) : (
                  upcomingAssessments.map((item: CompletedAssessment, index: number) => {
                    const duration = item.duration;

                    const expiryDateTime = dayjs(item.assessment_start_time)
                      .add(item.assessment_expiry, "second")
                      .format("DD MMM YYYY, hh:mm A");

                    const hours = Math.floor(duration / 60);
                    const minutes = duration % 60;

                    const getDurationText = () => {
                      if (hours > 0 && minutes > 0) {
                        return `${hours} Hour${hours > 1 ? "s" : ""
                          } ${minutes} Min`;
                      }

                      if (hours > 0) {
                        return `${hours} Hour${hours > 1 ? "s" : ""}`;
                      }

                      return `${minutes} Min`;
                    };

                    const subjectsList = [
                      ...(item.number_of_skill_questions ? ["Jozuna"] : []),
                      ...(item.category_list || []),
                      ...(item.curriculum_list || []),
                    ];

                    return (
                      <div
                        key={item.assessment_entity_id || item.assessment_id || `upcoming-${index}`}
                        className="v2-assessment-card"
                        style={{ border: "1px solid #BA8A00" }}
                      >
                        {/* TOP */}
                        <div className="v2-card-top">
                          <div className="v2-assessment-badges">
                            {item.number_of_skill_questions ? (
                              <span className="v2-assessment-badge">
                                JOZUNA
                              </span>
                            ) : null}

                            {item.number_of_category_questions ? (
                              <span className="v2-assessment-badge">
                                Curriculum
                              </span>
                            ) : null}

                            {item.number_of_curriculum_questions ? (
                              <span className="v2-assessment-badge">
                                Industry
                              </span>
                            ) : null}
                          </div>
                        </div>

                        {/* TITLE */}
                        <h3 className="v2-assessment-title">
                          {getAssessmentTitle(item)}
                        </h3>

                        <div className="v2-listAssessmentdivider"></div>

                        {/* DETAILS */}
                        <div className="v2-card-details">
                          <div className="v2-card-row-inline">
                            <div className="v2-card-item">
                              <img src={CardClockIcon} />

                              <span>
                                {item.duration}{" "}
                                <span className="light-text">Minutes</span>
                              </span>
                            </div>

                            <div className="v2-card-item">
                              <img src={CardQuesIcon} />

                              <span>
                                {item.total_question}{" "}
                                <span className="light-text">Questions</span>
                              </span>
                            </div>
                          </div>

                          <div className="v2-card-row-inline">
                            <div className="v2-card-item">
                              <img src={StartClock} />

                              <span>{item.assessment_start_time}</span>
                            </div>

                            <div className="v2-card-item">
                              <img src={EndClock} />

                              {dayjs(item.assessment_start_time)
                                .add(item.assessment_expiry, "second")
                                .format("YYYY/MM/DD, HH:mm:ss")}
                            </div>
                          </div>

                          <div className="v2-card-item">
                            <img src={CardSubIcon} />

                            <span>
                              {subjectsList.length}

                              <span
                                style={{
                                  marginLeft: "2px",
                                  fontWeight: 300,
                                }}
                              >
                                {" "}
                                {subjectsList.length > 1
                                  ? "Subjects Distribution"
                                  : "Subject Distribution"}
                              </span>
                            </span>

                            {subjectsList.length >= 1 && (
                              <span
                                className="v2-view-all"
                                onClick={() => {
                                  setSelectedSubjects({
                                    title: getAssessmentTitle(item),
                                    total:
                                      (item.number_of_skill_questions ? 1 : 0) +
                                      (item.number_of_category_questions || 0) +
                                      (item.number_of_curriculum_questions ||
                                        0),

                                    jozuna: item.number_of_skill_questions
                                      ? ["JOZUNA Skills"]
                                      : [],

                                    curriculum: (item.category_list || []).map(
                                      (c: any) => c.category_name,
                                    ),

                                    industry: (item.curriculum_list || []).map(
                                      (c: any) => c.curriculum_name,
                                    ),
                                  });

                                  setShowSubjectPopup(true);
                                }}
                              >
                                View All
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </>
            )}
            {activeTab === "expired" &&
              (filteredAssessmentList.length === 0 ? (
                <div
                  style={{
                    width: "100%",
                    height: "calc(100vh - 220px)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginLeft: "100%",
                  }}
                >
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: 600,
                      color: "#666",
                      textAlign: "center",
                    }}
                  >
                    No Expired Assessments Found
                  </div>
                </div>
              ) : (
                filteredAssessmentList.map(
                  (item: CompletedAssessment, index: number) => {
                    const duration = item.duration;

                    const hours = Math.floor(duration / 60);
                    const minutes = duration % 60;

                    const subjectsList = [
                      ...(item.number_of_skill_questions ? ["Jozuna"] : []),
                      ...(item.category_list || []),
                      ...(item.curriculum_list || []),
                    ];

                    const isCompleted =
                      item.status === "COMPLETED" || item.status === "SCORED";

                    return (
                      <div
                        key={item.assessment_entity_id || item.assessment_id || `expired-${index}`}
                        className={`v2-assessment-cardCompleted`}
                      >
                        {/* TOP */}
                        <div
                          className="v2-card-top"
                          style={{ justifyContent: "space-between" }}
                        >
                          <div className="v2-assessment-badges">
                            {item.number_of_skill_questions ? (
                              <span className="v2-assessment-badge">
                                JOZUNA
                              </span>
                            ) : null}

                            {item.number_of_category_questions ? (
                              <span className="v2-assessment-badge">
                                Curriculum
                              </span>
                            ) : null}

                            {item.number_of_curriculum_questions ? (
                              <span className="v2-assessment-badge">
                                Industry
                              </span>
                            ) : null}
                          </div>

                          <div
                            style={{
                              color: isCompleted ? "green" : "red",
                              display: "flex",
                              alignItems: "center",
                              fontWeight: 600,
                            }}
                          >
                            <img
                              src={isCompleted ? completed : missed}
                              alt="Status"
                              style={{ marginRight: "5px" }}
                            />

                            {isCompleted ? "Completed" : "Missed"}
                          </div>
                        </div>

                        {/* TITLE */}
                        <h3 className="v2-assessment-title">
                          {getAssessmentTitle(item)}
                        </h3>

                        <div className="v2-listAssessmentdivider"></div>

                        {/* DETAILS */}
                        <div className="v2-card-details">
                          {/* ROW 1 */}
                          <div className="v2-card-row-inline">
                            <div className="v2-card-item">
                              <img src={CardClockIcon} />

                              <span>
                                {item.duration}{" "}
                                <span className="light-text">Minutes</span>
                              </span>
                            </div>

                            <div className="v2-card-item">
                              <img src={CardQuesIcon} />

                              <span>
                                {item.total_question}{" "}
                                <span className="light-text">Questions</span>
                              </span>
                            </div>
                          </div>

                          {/* ROW 2 */}
                          <div className="v2-card-row-inline">
                            <div className="v2-card-item">
                              <img src={StartClock} />

                              <span>{item.assessment_start_time}</span>
                            </div>

                            <div className="v2-card-item">
                              <img src={EndClock} />

                              <span>
                                {dayjs(item.assessment_start_time)
                                  .add(item.assessment_expiry, "second")
                                  .format("YYYY/MM/DD, HH:mm:ss")}
                              </span>
                            </div>
                          </div>

                          {/* SUBJECTS */}
                          <div className="v2-card-item">
                            <img src={CardSubIcon} />

                            <span>
                              {subjectsList.length}

                              <span
                                style={{
                                  marginLeft: "2px",
                                  fontWeight: "300",
                                }}
                              >
                                {" "}
                                {subjectsList.length > 1
                                  ? "Subjects Distribution"
                                  : "Subject Distribution"}
                              </span>
                            </span>

                            {subjectsList.length >= 1 && (
                              <span
                                className="v2-view-all"
                                onClick={() => {
                                  setSelectedSubjects({
                                    title: getAssessmentTitle(item),
                                    total:
                                      (item.number_of_skill_questions ? 1 : 0) +
                                      (item.number_of_category_questions || 0) +
                                      (item.number_of_curriculum_questions ||
                                        0),

                                    jozuna: item.number_of_skill_questions
                                      ? ["JOZUNA Skills"]
                                      : [],

                                    curriculum: (item.category_list || []).map(
                                      (c: any) => c.category_name,
                                    ),

                                    industry: (item.curriculum_list || []).map(
                                      (c: any) => c.curriculum_name,
                                    ),
                                  });

                                  setShowSubjectPopup(true);
                                }}
                              >
                                View All
                              </span>
                            )}
                          </div>
                        </div>

                        {/* BOTTOM */}
                        <br />

                        <div className="v2-card-bottom">
                          <button
                            className="v2-start-btn"
                            disabled={!isCompleted}
                            onClick={() => {
                              dispatch(
                                setSelectedToggle("Assessments Results"),
                              );

                              navigate("/dashboard/student/skills-details", {
                                state: {
                                  assessmentId: item.assessment_id,

                                  title: item.number_of_skill_questions
                                    ? "Jozuna Skills Performance"
                                    : item.number_of_category_questions
                                      ? "Curriculum Performance"
                                      : item.number_of_curriculum_questions
                                        ? "Program Performance"
                                        : "",

                                  heading: item.number_of_skill_questions
                                    ? "Jozuna Skills"
                                    : item.number_of_category_questions
                                      ? "Curriculum"
                                      : item.number_of_curriculum_questions
                                        ? "Professional"
                                        : "",

                                  assessmentType: item.number_of_skill_questions
                                    ? "JOZUNA"
                                    : item.number_of_category_questions
                                      ? "Curriculum"
                                      : item.number_of_curriculum_questions
                                        ? "Industry"
                                        : "",

                                  toggleOptions: item.number_of_skill_questions
                                    ? ["Assessments Results", "Skills Timeline"]
                                    : item.number_of_curriculum_questions
                                      ? [
                                        "Assessments Results",
                                        "Progress Timeline",
                                      ]
                                      : item.number_of_category_questions
                                        ? [
                                          "Pop-Quiz",
                                          "Assessments Results",
                                          "Progress Timeline",
                                        ]
                                        : [],

                                  slidebarProgressOptions:
                                    !item.number_of_skill_questions
                                      ? [
                                        "Overall Progress",
                                        "Concepts Performance",
                                      ]
                                      : "",

                                  slidebarResultsOptions:
                                    !item.number_of_skill_questions
                                      ? [
                                        "Overall Results",
                                        "View Concept Analytics",
                                      ]
                                      : "",
                                  is_graded_quiz: isGradedQuiz,
                                },
                              });
                            }}
                          >
                            View Result
                          </button>
                        </div>
                      </div>
                    );
                  },
                )
              ))}
          </div>
        </div>
        <CardBottomOverlay
          cardHeight="50%"
          cardWidth="60%"
          isOpen={showSubjectPopup}
          onClose={() => setShowSubjectPopup(false)}
        >
          {selectedSubjects && ( // ✅ IMPORTANT GUARD
            <div className="v2-subject-popup">
              <h2 className="v2-popup-title">{selectedSubjects.title}</h2>

              <div className="v2-popup-subtitle">
                <img src={CardSubIcon} />
                <span>
                  {selectedSubjects.total}{" "}
                  {selectedSubjects.total > 1
                    ? "Subjects Distribution"
                    : "Subject Distribution"}
                </span>
              </div>

              <div className="v2-popup-grid">
                {/* JOZUNA */}
                {selectedSubjects.jozuna?.length > 0 && (
                  <div className="v2-popup-card">
                    <div className="v2-popup-card-title">Jozuna:</div>
                    <ul>
                      {selectedSubjects.jozuna.map((item, i) => (
                        <div key={i}>{item}</div>
                      ))}
                    </ul>
                  </div>
                )}

                {/* CURRICULUM */}
                {selectedSubjects.curriculum?.length > 0 && (
                  <div className="v2-popup-card">
                    <div className="v2-popup-card-title">Curriculum:</div>
                    <ul>
                      {selectedSubjects.curriculum.map((item, i) => (
                        <div key={i}>{item}</div>
                      ))}
                    </ul>
                  </div>
                )}

                {/* INDUSTRY */}
                {selectedSubjects.industry?.length > 0 && (
                  <div className="v2-popup-card">
                    <div className="v2-popup-card-title">Industry:</div>
                    <ul>
                      {selectedSubjects.industry.map((item, i) => (
                        <div key={i}>{item}</div>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardBottomOverlay>
        {isModalOpen && (
          <RightModalRecommendation
            isOpen={isModalOpen}
            toggleModal={toggleModal}
          />
        )}
      </div>
    </div>
  );
};

export default AssessmentListPage;
